import axios from 'axios'
import laroute from '~/laroute'
import store from '~/store/index'
import Commission from '~/_shared/models/Commission'
import CommissionVoucher from '~/_shared/models/CommissionVoucher'

// state
export const state = {
  sales_reps: [],
  rates: [],
  activeSalesRepSalesOrders: [],
  invoicedSalesRepSalesOrders: [],
  allowedItemMasterIds: [],
  items: [],
  item: new Commission(),
  salesRepPaymentTerms: [],
  vouchers: [],
  voucher: new CommissionVoucher(),
  current: null
}

// getters
export const getters = {
  sales_reps: state => state.sales_reps,
  rates: state => state.rates,
  activeSalesRepSalesOrders: state => state.activeSalesRepSalesOrders,
  invoicedSalesRepSalesOrders: state => state.invoicedSalesRepSalesOrders,
  allowedSalesOrders: state => state.allowedSalesOrders,
  allowedItemMasterIds: state => state.allowedItemMasterIds,
  items: state => state.items,
  item: state => state.item,
  salesRepPaymentTerms: state => state.salesRepPaymentTerms,
  vouchers: state => state.vouchers,
  current: state => state.current,
  voucher: state => state.voucher
}

// mutations
export const mutations = {
  SALES_REPS (state, data) {
    state.sales_reps = data
  },
  RATES (state, data) {
    state.rates = data
  },

  ACTIVE_SALES_REP_SALES_ORDERS (state, data) {
    state.activeSalesRepSalesOrders = data
  },

  INVOICED_SALES_REP_SALES_ORDERS (state, data) {
    state.invoicedSalesRepSalesOrders = data
  },

  ALLOWED_ITEM_MASTERS (state, data) {
    state.allowedItemMasterIds = data
  },

  ITEMS (state, data) {
    state.items = data
  },

  ITEM (state, data) {
    state.item = Object.assign(state.item, data)
  },

  SALES_REP_PAYMENT_TERMS (state, data) {
    state.salesRepPaymentTerms = data
  },

  VOUCHERS (state, data) {
    state.vouchers = data
  },

  CURRENT (state, data) {
    state.current = data
  },

  VOUCHER (state, data) {
    state.voucher = Object.assign(state.item, data)
  }
}

// actions
export const actions = {
  async fetchSalesReps ({ commit }, filterData) {
    await axios.get(
      laroute.route('fetch_commissions_sales_reps', { companyAbbr: store.getters['auth/company'], ...filterData })
    ).then(res => {
      commit('SALES_REPS', res.data)
    })
  },

  async fetchRates ({ commit }) {
    await axios.get(
      laroute.route('fetch_commissions_rates', { companyAbbr: store.getters['auth/company'] })
    ).then(res => {
      commit('RATES', res.data)
    })
  },

  async updateRate ({ commit }, item) {
    await axios.patch(laroute.route('update_commission_rate', {
      companyAbbr: store.getters['auth/company'],
      id: item.id
    }), { ...item })
  },

  async createRate ({ commit }, rate) {
    await axios.post(laroute.route('create_commission_rate', { companyAbbr: store.getters['auth/company'] }), { rate })
  },

  async deleteRate ({ commit }, id) {
    await axios.delete(laroute.route('delete_commission_rate', { companyAbbr: store.getters['auth/company'], id: id }))
  },

  async fetchActiveSalesRepSalesOrders ({ commit }, salesRepId) {
    await axios.get(laroute.route('get-active-sales-rep-sales-orders', {
      companyAbbr: store.getters['auth/company'],
      salesRepId
    }))
      .then((res) => {
        commit('ACTIVE_SALES_REP_SALES_ORDERS', res.data)
      })
  },

  async fetchInvoicedSalesRepSalesOrders ({ commit }, salesRepId) {
    await axios.get(laroute.route('get-invoiced-sales-rep-sales-orders', {
      companyAbbr: store.getters['auth/company'],
      salesRepId
    }))
      .then((res) => {
        commit('INVOICED_SALES_REP_SALES_ORDERS', res.data)
      })
  },

  async fetchAllowedItemMasterIds ({ commit }, salesRepId) {
    await axios.get(laroute.route('get-commissions-allowed-item-masters', {
      companyAbbr: store.getters['auth/company'],
      salesRepId
    }))
      .then((res) => {
        commit('ALLOWED_ITEM_MASTERS', res.data)
      })
  },

  async salesRepProfileFilter ({ commit }, { filterData, salesRepId }) {
    await axios.get(laroute.route('commissions-sales-rep-profile-filter', {
      companyAbbr: store.getters['auth/company'],
      salesRepId,
      type: filterData.type,
      value: filterData.value,
      showExcluded: filterData.showExcluded
    }))
      .then((res) => {
        commit('ITEMS', res.data)
      })
  },

  cleanItems ({ commit }) {
    commit('ITEMS', [])
  },

  async fetchCommissions ({ commit }, filterData) {
    await axios.get(laroute.route('commissions-filter', { companyAbbr: store.getters['auth/company'], ...filterData }))
      .then((res) => {
        commit('ITEMS', res.data)
      })
  },

  async massUpdate ({ commit }, items) {
    await axios.patch(laroute.route('commissions-mass-update', { companyAbbr: store.getters['auth/company'] }), { items })
  },

  async deleteItem ({ commit }, id) {
    await axios.delete(laroute.route('commissions-delete-item', { companyAbbr: store.getters['auth/company'], id: id }))
  },

  async create ({ commit }, data) {
    await axios.post(laroute.route('commissions-create', { companyAbbr: store.getters['auth/company'] }), { ...data })
  },

  async fetchOne ({ commit }, id) {
    await axios.get(laroute.route('commissions-getOne', {
      companyAbbr: store.getters['auth/company'],
      id
    })).then((res) => {
      commit('ITEM', res.data)
    })
  },

  async update ({ commit }, commission) {
    await axios.patch(laroute.route('commissions-update', {
      companyAbbr: store.getters['auth/company'],
      id: commission.id
    }), {
      ...commission
    })
  },

  async getSalesRepPaymentTerms ({ commit }, { customerId, salesRepId }) {
    await axios.get(laroute.route('get-commissions-payment-terms', {
      companyAbbr: store.getters['auth/company'],
      salesRepId,
      customerId
    }))
      .then((res) => {
        commit('SALES_REP_PAYMENT_TERMS', res.data)
      })
  },

  async createSalesRepPaymentTerms ({ commit }, { formData, salesRepId }) {
    await axios.post(laroute.route('create-commissions-payment-terms', {
      companyAbbr: store.getters['auth/company'], salesRepId: salesRepId
    }), { formData })
  },

  async updateSalesRepPaymentTerms ({ commit }, { salesRepId, items }) {
    await axios.patch(laroute.route('update-commission-payment-term', { companyAbbr: store.getters['auth/company'], salesRepId: salesRepId }), { items })
  },

  async deleteSalesRepPaymentTerm ({ commit }, id) {
    await axios.delete(laroute.route('delete-commission-payment-term', { companyAbbr: store.getters['auth/company'], id: id }))
  },

  async fetchCommissionVouchers ({ commit }, { ...filters }) {
    await axios.get(laroute.route('get-commission-vouchers', { companyAbbr: store.getters['auth/company'], ...filters })).then((res) => {
      commit('VOUCHERS', res.data)
    })
  },

  async fetchOneCommissionVoucher ({ commit }, id) {
    await axios.get(laroute.route('get-one-commission-voucher', {
      companyAbbr: store.getters['auth/company'],
      id
    })).then((res) => {
      commit('VOUCHER', res.data)
    })
  },

  async createCommissionVoucher ({ commit }, { ...formData }) {
    await axios.post(laroute.route('create-commission-voucher', {
      companyAbbr: store.getters['auth/company']
    }), { formData }).then((res) => {
      commit('CURRENT', res.data)
    })
  },

  async updateCommissionVoucher ({ commit }, { id, voucher }) {
    await axios.patch(laroute.route('update-commission-voucher', { companyAbbr: store.getters['auth/company'], id: id }), { data: voucher })
      .then((res) => {
        commit('CURRENT', res.data)
      })
  },

  async deleteCommissionVoucher ({ commit }, id) {
    await axios.delete(laroute.route('delete-commission-voucher', { companyAbbr: store.getters['auth/company'], id: id }))
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/commissions.js